import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calculator, Car, Calendar, DollarSign, Fuel, ChevronRight, Info, Zap, RefreshCw, Download } from "lucide-react";
import { jsPDF } from "jspdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Тарифы для расчёта (ЕАЭС для Беларуси)
// Авто до 3 лет: % от стоимости, но не менее X евро за куб.см
const TARIFFS = {
  under3: {
    rates: [
      { maxPrice: 8500, percent: 54, perCc: 2.5, label: "до €8 500" },
      { maxPrice: 16700, percent: 48, perCc: 3.5, label: "€8 500 - €16 700" },
      { maxPrice: 42300, percent: 48, perCc: 5.5, label: "€16 700 - €42 300" },
      { maxPrice: 84500, percent: 48, perCc: 7.5, label: "€42 300 - €84 500" },
      { maxPrice: 169000, percent: 48, perCc: 15, label: "€84 500 - €169 000" },
      { maxPrice: Infinity, percent: 48, perCc: 20, label: "более €169 000" },
    ],
  },
  // Авто 3-5 лет - ставка за куб.см в зависимости от объёма двигателя
  from3to5: {
    rates: [
      { maxCc: 1000, perCc: 1.5, label: "до 1000 см³" },
      { maxCc: 1500, perCc: 1.7, label: "1000-1500 см³" },
      { maxCc: 1800, perCc: 2.5, label: "1500-1800 см³" },
      { maxCc: 2300, perCc: 2.7, label: "1800-2300 см³" },
      { maxCc: 3000, perCc: 3.0, label: "2300-3000 см³" },
      { maxCc: Infinity, perCc: 3.6, label: "более 3000 см³" },
    ],
  },
  // Авто более 5 лет - повышенные ставки за куб.см
  over5: {
    rates: [
      { maxCc: 1000, perCc: 3.0, label: "до 1000 см³" },
      { maxCc: 1500, perCc: 3.2, label: "1000-1500 см³" },
      { maxCc: 1800, perCc: 3.5, label: "1500-1800 см³" },
      { maxCc: 2300, perCc: 4.8, label: "1800-2300 см³" },
      { maxCc: 3000, perCc: 5.0, label: "2300-3000 см³" },
      { maxCc: Infinity, perCc: 5.7, label: "более 3000 см³" },
    ],
  },
  // Электромобили - ОСВОБОЖДЕНИЕ от таможенных пошлин
  // Указ №92 от 12.03.2020, Указ №428 от 11.12.2025, Решение ЕЭК №111 от 05.12.2025
  // В пределах квоты ЕАЭС - 0% таможенная пошлина + 0% НДС для авто не старше 5 лет
  electric: {
    under3: { percent: 0, label: "0% (освобождение)", exempt: true },
    from3to5: { percent: 0, label: "0% (освобождение)", exempt: true },
    over5: { percent: 15, label: "15% от стоимости", exempt: false }, // старше 5 лет - без льгот
  },
};

// Стоимость доставки по странам (в USD)
const DELIVERY_COST = {
  china: { min: 2800, max: 2800, label: "Китай" },
  korea: { min: 4900, max: 4900, label: "Корея" },
  usa: { min: 4900, max: 4900, label: "США" },
};

// Дополнительные расходы
const ADDITIONAL_COSTS = {
  certification: 300, // СБКТС
  broker: 200, // Услуги брокера
  parking: 100, // Склад временного хранения
};

const EUR_TO_USD = 1.08; // Fallback курс EUR/USD
const USD_TO_EUR = 1 / EUR_TO_USD;

// API для получения актуальных курсов
const EXCHANGE_API_URL = "https://api.frankfurter.app/latest?from=EUR&to=USD";
const NBRB_API_URL = "https://api.nbrb.by/exrates/rates/USD?parammode=2"; // Курс USD от НБРБ

// Утилизационный сбор в белорусских рублях (BYN)
// Постановление Совмина №50 от 28.01.2025 (с 10.02.2025)
const UTILIZATION_FEE_BYN = {
  // ДВС автомобили (физлица, категория M1)
  gasoline: {
    under3: 544,      // до 3 лет
    from3to7: 1089,   // от 3 до 7 лет
    over7: 2757,      // старше 7 лет
  },
  // Электромобили (физлица, категория M1)
  electric: {
    under3: 544.5,    // до 3 лет
    from3to7: 1089,   // от 3 до 7 лет
    over7: 2757.36,   // старше 7 лет
  },
};

// Услуга подбора автомобиля (BYN)
const CAR_SELECTION_FEE_BYN = 640;

const countries = [
  { value: "china", label: "Китай", flag: "🇨🇳" },
  { value: "korea", label: "Корея", flag: "🇰🇷" },
  { value: "usa", label: "США", flag: "🇺🇸" },
];

const ageOptions = [
  { value: "under3", label: "До 3 лет" },
  { value: "from3to5", label: "От 3 до 5 лет" },
  { value: "over5", label: "Более 5 лет" },
];

const carTypeOptions = [
  { value: "gasoline", label: "Бензин/Дизель", icon: "⛽" },
  { value: "electric", label: "Электромобиль", icon: "⚡" },
];

export default function CalculatorSection() {
  const [formData, setFormData] = useState({
    country: "",
    carType: "gasoline",
    age: "",
    engineVolume: "",
    carPrice: "",
    decree140: false, // Льгота по Указу №140
  });
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [exchangeRate, setExchangeRate] = useState({ 
    eurToUsd: EUR_TO_USD, 
    usdToByn: 3.27, // Fallback курс USD/BYN
    lastUpdated: null, 
    isLoading: true 
  });

  // Fetch exchange rates on mount
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        // Fetch EUR/USD rate
        const eurResponse = await fetch(EXCHANGE_API_URL);
        const eurData = await eurResponse.json();
        
        // Fetch USD/BYN rate from NBRB
        const bynResponse = await fetch(NBRB_API_URL);
        const bynData = await bynResponse.json();
        
        setExchangeRate({
          eurToUsd: eurData.rates?.USD || EUR_TO_USD,
          usdToByn: bynData.Cur_OfficialRate || 3.27,
          lastUpdated: new Date().toLocaleString("ru-RU"),
          isLoading: false,
        });
      } catch (error) {
        console.error("Failed to fetch exchange rates:", error);
        setExchangeRate(prev => ({ ...prev, isLoading: false }));
      }
    };
    fetchExchangeRates();
  }, []);

  const refreshExchangeRate = async () => {
    setExchangeRate(prev => ({ ...prev, isLoading: true }));
    try {
      const eurResponse = await fetch(EXCHANGE_API_URL);
      const eurData = await eurResponse.json();
      
      const bynResponse = await fetch(NBRB_API_URL);
      const bynData = await bynResponse.json();
      
      setExchangeRate({
        eurToUsd: eurData.rates?.USD || EUR_TO_USD,
        usdToByn: bynData.Cur_OfficialRate || 3.27,
        lastUpdated: new Date().toLocaleString("ru-RU"),
        isLoading: false,
      });
    } catch (error) {
      console.error("Failed to fetch exchange rates:", error);
      setExchangeRate(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Don't reset result when changing car type
    if (name !== "carType") {
      setResult(null);
    }
  };

  const calculateCost = () => {
    const { country, carType, age, engineVolume, carPrice, decree140 } = formData;
    
    if (!country || !age || !carPrice) {
      return;
    }
    
    // Для бензин/дизель нужен объём двигателя
    if (carType === "gasoline" && !engineVolume) {
      return;
    }

    setIsCalculating(true);
    
    setTimeout(() => {
      const priceUSD = parseFloat(carPrice);
      const currentEurToUsd = exchangeRate.eurToUsd;
      const currentUsdToEur = 1 / currentEurToUsd;
      const priceEUR = priceUSD * currentUsdToEur;
      const cc = carType === "electric" ? 0 : parseInt(engineVolume);
      
      let customsDuty = 0;
      let appliedRate = null;
      let calculationMethod = "";
      let percentDuty = 0;
      let ccDuty = 0;
      
      if (carType === "electric") {
        // Электромобили - освобождение от таможенной пошлины (Указ №92, №428, Решение ЕЭК №111)
        appliedRate = TARIFFS.electric[age];
        if (appliedRate.exempt) {
          // До 5 лет - полное освобождение от пошлины и НДС
          percentDuty = 0;
          customsDuty = 0;
          calculationMethod = "0% (освобождение по Указу №92/428)";
        } else {
          // Старше 5 лет - стандартная ставка 15%
          percentDuty = priceEUR * (appliedRate.percent / 100);
          customsDuty = percentDuty * currentEurToUsd;
          calculationMethod = `${appliedRate.percent}% от стоимости (старше 5 лет)`;
        }
      } else if (age === "under3") {
        // Для авто до 3 лет: % от стоимости, но не менее X евро за куб.см
        appliedRate = TARIFFS.under3.rates.find(r => priceEUR <= r.maxPrice);
        percentDuty = priceEUR * (appliedRate.percent / 100);
        ccDuty = cc * appliedRate.perCc;
        
        if (percentDuty >= ccDuty) {
          customsDuty = percentDuty * currentEurToUsd;
          calculationMethod = `${appliedRate.percent}% от стоимости`;
        } else {
          customsDuty = ccDuty * currentEurToUsd;
          calculationMethod = `€${appliedRate.perCc} × ${cc} см³`;
        }
      } else if (age === "from3to5") {
        // Для авто 3-5 лет: ставка за куб.см
        appliedRate = TARIFFS.from3to5.rates.find(r => cc <= r.maxCc);
        ccDuty = cc * appliedRate.perCc;
        customsDuty = ccDuty * currentEurToUsd;
        calculationMethod = `€${appliedRate.perCc} × ${cc} см³`;
      } else {
        // Для авто более 5 лет: повышенные ставки за куб.см
        appliedRate = TARIFFS.over5.rates.find(r => cc <= r.maxCc);
        ccDuty = cc * appliedRate.perCc;
        customsDuty = ccDuty * currentEurToUsd;
        calculationMethod = `€${appliedRate.perCc} × ${cc} см³`;
      }
      
      // Применяем льготу 50% по Указу №140
      let customsDutyBeforeDiscount = customsDuty;
      let decree140Discount = 0;
      if (decree140) {
        decree140Discount = customsDuty * 0.5;
        customsDuty = customsDuty * 0.5;
        calculationMethod += " (−50% Указ №140)";
      }
      
      const delivery = DELIVERY_COST[country];
      const avgDelivery = (delivery.min + delivery.max) / 2;
      
      const certification = ADDITIONAL_COSTS.certification;
      const broker = ADDITIONAL_COSTS.broker;
      const parking = ADDITIONAL_COSTS.parking;
      
      // Утилизационный сбор в BYN (Постановление Совмина №50 от 28.01.2025)
      // Зависит от возраста авто
      let utilizationFeeBYN;
      let utilizationAgeCategory;
      
      // Определяем категорию возраста для утильсбора (до 3 лет, 3-7 лет, более 7 лет)
      if (age === "under3") {
        utilizationAgeCategory = "under3";
      } else if (age === "from3to5") {
        utilizationAgeCategory = "from3to7"; // 3-5 лет входит в категорию 3-7 лет
      } else {
        utilizationAgeCategory = "over7"; // более 5 лет = более 7 лет для утильсбора
      }
      
      if (carType === "electric") {
        utilizationFeeBYN = UTILIZATION_FEE_BYN.electric[utilizationAgeCategory];
      } else {
        utilizationFeeBYN = UTILIZATION_FEE_BYN.gasoline[utilizationAgeCategory];
      }
      
      // Конвертация утильсбора в USD по курсу НБРБ
      const currentUsdToByn = exchangeRate.usdToByn;
      const utilizationFeeUSD = Math.round(utilizationFeeBYN / currentUsdToByn);
      
      // Услуга подбора автомобиля
      const carSelectionFeeBYN = CAR_SELECTION_FEE_BYN;
      const carSelectionFeeUSD = Math.round(carSelectionFeeBYN / currentUsdToByn);
      
      const totalAdditional = certification + broker + parking + utilizationFeeUSD + carSelectionFeeUSD;
      const total = priceUSD + customsDuty + avgDelivery + totalAdditional;
      
      setResult({
        carPrice: priceUSD,
        carPriceEUR: Math.round(priceEUR),
        customsDuty: Math.round(customsDuty),
        customsDutyBeforeDiscount: Math.round(customsDutyBeforeDiscount),
        decree140Discount: Math.round(decree140Discount),
        decree140Applied: decree140,
        customsDutyEUR: Math.round(carType === "electric" ? percentDuty : (age === "under3" ? Math.max(percentDuty, ccDuty) : ccDuty)),
        delivery: Math.round(avgDelivery),
        deliveryRange: delivery,
        certification,
        broker,
        parking,
        utilizationFeeBYN,
        utilizationFeeUSD,
        carSelectionFeeBYN,
        carSelectionFeeUSD,
        usdToByn: currentUsdToByn,
        total: Math.round(total),
        appliedRate,
        calculationMethod,
        age,
        carType,
        engineVolume: cc,
        exchangeRate: currentEurToUsd,
      });
      
      setIsCalculating(false);
    }, 800);
  };

  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const exportToPDF = () => {
    if (!result) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFillColor(15, 15, 15);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.text("SBE.by", 20, 25);
    doc.setFontSize(10);
    doc.text("Import avtomobilej iz Kitaja, Korei, SSHA", 20, 33);
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    
    // Title
    doc.setFontSize(18);
    doc.text("Raschet stoimosti rastamozhivanija", 20, 55);
    
    // Date
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    const today = new Date().toLocaleDateString('ru-RU');
    doc.text(`Data rascheta: ${today}`, 20, 63);
    
    doc.setTextColor(0, 0, 0);
    
    // Car info box
    doc.setFillColor(245, 245, 245);
    doc.rect(15, 70, pageWidth - 30, 35, 'F');
    doc.setFontSize(12);
    doc.text("Parametry avtomobilja:", 20, 82);
    doc.setFontSize(10);
    
    const countryLabels = { china: "Kitaj", korea: "Koreja", usa: "SSHA" };
    const ageLabels = { under3: "do 3 let", from3to5: "3-5 let", over5: "bolee 5 let" };
    const typeLabels = { gasoline: "Benzin/Dizel", electric: "Elektromobil" };
    
    doc.text(`Strana: ${countryLabels[country] || country}`, 25, 92);
    doc.text(`Vozrast: ${ageLabels[result.age]}`, 70, 92);
    doc.text(`Tip: ${typeLabels[result.carType]}`, 115, 92);
    if (result.carType !== "electric") {
      doc.text(`Objem: ${result.engineVolume} sm3`, 160, 92);
    }
    
    // Breakdown table
    let yPos = 115;
    doc.setFontSize(12);
    doc.text("Detalizacija raschetov:", 20, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    const lineHeight = 8;
    
    const addLine = (label, value, highlight = false) => {
      if (highlight) {
        doc.setFillColor(220, 252, 231);
        doc.rect(15, yPos - 5, pageWidth - 30, lineHeight, 'F');
      }
      doc.text(label, 20, yPos);
      doc.text(value, pageWidth - 20, yPos, { align: 'right' });
      yPos += lineHeight;
    };
    
    addLine("Stoimost avtomobilja", `$${result.carPrice.toLocaleString()} (EUR${result.carPriceEUR.toLocaleString()})`);
    
    if (result.decree140Applied) {
      addLine("Tamozhennaja poshlina (do skidki)", `$${result.customsDutyBeforeDiscount.toLocaleString()}`);
      addLine("Skidka po Ukazu No140 (-50%)", `-$${result.decree140Discount.toLocaleString()}`, true);
      addLine("Tamozhennaja poshlina (itogo)", `$${result.customsDuty.toLocaleString()}`);
    } else {
      addLine("Tamozhennaja poshlina", `$${result.customsDuty.toLocaleString()}`);
    }
    
    addLine("Dostavka", `~$${result.delivery.toLocaleString()}`);
    addLine(`Utilizacionnyj sbor (${result.utilizationFeeBYN} BYN)`, `$${result.utilizationFeeUSD}`);
    addLine(`Podbor avtomobilja (${result.carSelectionFeeBYN} BYN)`, `$${result.carSelectionFeeUSD}`);
    addLine("Sertifikacija (SBKTS)", `$${result.certification}`);
    addLine("Broker + SVH", `$${result.broker + result.parking}`);
    
    // Total box
    yPos += 5;
    doc.setFillColor(37, 99, 235);
    doc.rect(15, yPos, pageWidth - 30, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.text("ITOGO:", 20, yPos + 13);
    doc.setFontSize(18);
    doc.text(`$${result.total.toLocaleString()}`, pageWidth - 20, yPos + 13, { align: 'right' });
    
    // Footer notes
    yPos += 30;
    doc.setTextColor(128, 128, 128);
    doc.setFontSize(8);
    doc.text("* Raschet javljaetsja predvaritelnym. Tochnuju stoimost utochnjajte u menedzhera.", 20, yPos);
    doc.text(`Kurs NBRB: 1 USD = ${result.usdToByn?.toFixed(4)} BYN | Kurs: 1 EUR = ${result.exchangeRate?.toFixed(4)} USD`, 20, yPos + 5);
    
    // Contact info
    yPos += 15;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text("Kontakty:", 20, yPos);
    doc.setFontSize(9);
    doc.text("Sajt: sbe.by | Telegram: @sertbelinj", 20, yPos + 6);
    doc.text("Tel: +375 (29) 123-45-67", 20, yPos + 12);
    
    // Save
    doc.save(`SBE_raschet_${today.replace(/\./g, '-')}.pdf`);
  };

  return (
    <section 
      id="calculator" 
      className="py-20 md:py-24 bg-[#0F0F0F]"
      data-testid="calculator-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <span className="text-sm font-semibold tracking-widest uppercase text-[#F97316] mb-4 block">
            Калькулятор
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4">
            Рассчитайте стоимость
          </h2>
          <p className="text-base md:text-lg text-white/50 max-w-2xl mx-auto">
            Узнайте примерную стоимость автомобиля с учётом таможни и доставки
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Calculator Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-[#0A0A0A] rounded-none p-6 md:p-8"
            data-testid="calculator-form"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-none flex items-center justify-center bg-blue-600">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Параметры авто</h3>
                <p className="text-sm text-white/50">Заполните данные для расчёта</p>
              </div>
            </div>

            <div className="space-y-5">
              {/* Car Type Toggle */}
              <div>
                <Label className="text-sm font-medium text-white mb-2 block">
                  Тип автомобиля
                </Label>
                <div className="flex gap-2">
                  {carTypeOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleChange("carType", option.value)}
                      className={`flex-1 h-12 rounded-none text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        formData.carType === option.value
                          ? option.value === "electric" 
                            ? "bg-green-500 text-white"
                            : "bg-white text-black"
                          : "bg-[#0F0F0F] border border-white/10 text-white/50 hover:border-white/30"
                      }`}
                      data-testid={`cartype-btn-${option.value}`}
                    >
                      <span>{option.icon}</span>
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Country */}
              <div>
                <Label className="text-sm font-medium text-white mb-2 block">
                  Страна
                </Label>
                <Select 
                  value={formData.country} 
                  onValueChange={(value) => handleChange("country", value)}
                >
                  <SelectTrigger 
                    className="h-12 rounded-none border-white/10 bg-[#0F0F0F]"
                    data-testid="select-country"
                  >
                    <SelectValue placeholder="Выберите страну" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.value} value={country.value}>
                        <span className="flex items-center gap-2">
                          <span>{country.flag}</span>
                          {country.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Age */}
              <div>
                <Label className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                  Возраст автомобиля
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="w-4 h-4 text-white/40" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-sm">
                          Таможенные пошлины зависят от возраста авто. 
                          Для авто старше 5 лет применяются повышенные ставки.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <Select 
                  value={formData.age} 
                  onValueChange={(value) => handleChange("age", value)}
                >
                  <SelectTrigger 
                    className="h-12 rounded-none border-white/10 bg-[#0F0F0F]"
                    data-testid="select-age"
                  >
                    <SelectValue placeholder="Выберите возраст" />
                  </SelectTrigger>
                  <SelectContent>
                    {ageOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Engine Volume - only for gasoline */}
              {formData.carType === "gasoline" && (
              <div>
                <Label className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                  <Fuel className="w-4 h-4 text-white/50" />
                  Объём двигателя (см³)
                </Label>
                <Input
                  type="number"
                  value={formData.engineVolume}
                  onChange={(e) => handleChange("engineVolume", e.target.value)}
                  placeholder="Например: 1998"
                  className="h-12 rounded-none border-white/10 bg-[#0F0F0F]"
                  data-testid="input-engine"
                  min="500"
                  max="10000"
                />
                <p className="text-xs text-white/40 mt-1">
                  Укажите точный объём в кубических сантиметрах
                </p>
              </div>
              )}

              {/* Car Price */}
              <div>
                <Label className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-white/50" />
                  Стоимость авто (USD)
                </Label>
                <Input
                  type="number"
                  value={formData.carPrice}
                  onChange={(e) => handleChange("carPrice", e.target.value)}
                  placeholder="Например: 25000"
                  className="h-12 rounded-none border-white/10 bg-[#0F0F0F]"
                  data-testid="input-price"
                />
              </div>

              {/* Exchange Rate Info */}
              <div className="p-3 bg-[#0F0F0F] rounded-none border border-white/10 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-white/50">EUR/USD:</span>
                      <span className="text-sm font-semibold text-white">
                        {exchangeRate.isLoading ? "..." : exchangeRate.eurToUsd.toFixed(4)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-white/50">USD/BYN (НБРБ):</span>
                      <span className="text-sm font-semibold text-white">
                        {exchangeRate.isLoading ? "..." : exchangeRate.usdToByn.toFixed(4)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={refreshExchangeRate}
                    disabled={exchangeRate.isLoading}
                    className="p-1.5 rounded-lg hover:bg-[#F1F5F9] transition-colors"
                    title="Обновить курсы"
                    data-testid="refresh-rate-btn"
                  >
                    <RefreshCw className={`w-4 h-4 text-white/50 ${exchangeRate.isLoading ? "animate-spin" : ""}`} />
                  </button>
                </div>
                {exchangeRate.lastUpdated && (
                  <p className="text-xs text-white/40">Обновлено: {exchangeRate.lastUpdated}</p>
                )}
              </div>

              {/* Decree 140 Checkbox */}
              <div 
                className={`p-4 rounded-none border-2 transition-all cursor-pointer ${
                  formData.decree140 
                    ? "bg-green-500/10 border-green-500/30" 
                    : "bg-[#0F0F0F] border-white/10 hover:border-green-300"
                }`}
                onClick={() => handleChange("decree140", !formData.decree140)}
                data-testid="decree140-checkbox"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    formData.decree140 ? "bg-green-500" : "border-2 border-[#CBD5E1]"
                  }`}>
                    {formData.decree140 && (
                      <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                        <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className={`font-semibold ${formData.decree140 ? "text-green-700" : "text-white"}`}>
                      Льгота 50% по Указу №140
                    </p>
                    <p className="text-xs text-white/50 mt-1">
                      Для инвалидов I-II гр., многодетных семей, опекунов детей-инвалидов
                    </p>
                  </div>
                </div>
              </div>

              <Button
                onClick={calculateCost}
                disabled={!formData.country || !formData.age || (!formData.engineVolume && formData.carType === "gasoline") || !formData.carPrice || isCalculating}
                className="w-full h-14 bg-white hover:bg-gray-200 text-black rounded-none text-base font-semibold mt-2"
                data-testid="calculate-btn"
              >
                {isCalculating ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Расчёт...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Рассчитать стоимость
                    <ChevronRight className="w-5 h-5" />
                  </span>
                )}
              </Button>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex flex-col"
          >
            {result ? (
              <div 
                className="bg-[#050505] rounded-none p-6 md:p-8 text-white flex-1"
                data-testid="calculator-result"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-none flex items-center justify-center bg-blue-600">
                    <Car className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Результат расчёта</h3>
                    <p className="text-sm text-white/40">Примерная стоимость</p>
                  </div>
                </div>

                {/* Applied Tariff Info */}
                <div className="bg-[#0F0F0F] rounded-none p-4 mb-6">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-xs text-white/50">Применённый тариф:</p>
                    {result.carType === "electric" && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">⚡ Электро</span>
                    )}
                  </div>
                  <p className="text-sm text-white">
                    {result.carType === "electric" ? (
                      result.appliedRate?.exempt ? (
                        <>
                          <span className="text-green-400 font-bold">Освобождение от пошлины</span>
                          <br />
                          <span className="text-xs text-green-300">Указ №92/428, Решение ЕЭК №111 (до 5 лет)</span>
                        </>
                      ) : (
                        <>
                          <span className="text-[#F97316]">{result.appliedRate?.percent}%</span> от стоимости (старше 5 лет — без льгот)
                        </>
                      )
                    ) : result.age === "under3" ? (
                      <>
                        <span className="text-[#2563EB]">{result.appliedRate.percent}%</span> от стоимости, 
                        но не менее <span className="text-[#2563EB]">€{result.appliedRate.perCc}</span> за см³
                      </>
                    ) : (
                      <>
                        <span className="text-[#2563EB]">€{result.appliedRate.perCc}</span> за см³ 
                        (объём {result.appliedRate.label})
                      </>
                    )}
                  </p>
                  {!(result.carType === "electric" && result.appliedRate?.exempt) && (
                    <p className="text-xs text-[#F97316] mt-2">
                      Расчёт: {result.calculationMethod} = €{result.customsDutyEUR.toLocaleString()}
                    </p>
                  )}
                  <p className="text-xs text-white/50 mt-1">
                    Курс: 1 EUR = {result.exchangeRate?.toFixed(4) || "1.08"} USD
                  </p>
                </div>

                {/* Breakdown */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center py-3 border-b border-[#1E293B]">
                    <div>
                      <span className="text-white/40">Стоимость авто</span>
                      <span className="text-xs text-white/50 ml-2">(€{result.carPriceEUR.toLocaleString()})</span>
                    </div>
                    <span className="font-semibold">${result.carPrice.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-[#1E293B]">
                    <div>
                      <span className="text-white/40">Таможенная пошлина</span>
                      {result.decree140Applied && (
                        <span className="text-xs text-green-400 ml-2">(−50%)</span>
                      )}
                    </div>
                    <div className="text-right">
                      {result.decree140Applied && (
                        <span className="text-xs text-white/50 line-through mr-2">
                          ${result.customsDutyBeforeDiscount.toLocaleString()}
                        </span>
                      )}
                      <span className="font-semibold">${result.customsDuty.toLocaleString()}</span>
                    </div>
                  </div>
                  {result.decree140Applied && (
                    <div className="flex justify-between items-center py-3 border-b border-green-500/30 bg-green-500/10 -mx-4 px-4 rounded">
                      <span className="text-green-400 font-medium">Экономия по Указу №140</span>
                      <span className="font-bold text-green-400">−${result.decree140Discount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-3 border-b border-[#1E293B]">
                    <span className="text-white/40">
                      Доставка
                      <span className="text-xs ml-1 text-white/50">
                        (${result.deliveryRange.min}-{result.deliveryRange.max})
                      </span>
                    </span>
                    <span className="font-semibold">~${result.delivery.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-[#1E293B]">
                    <div>
                      <span className="text-white/40">Утилизационный сбор</span>
                      <span className="text-xs text-white/50 ml-2">
                        ({result.utilizationFeeBYN} BYN)
                      </span>
                    </div>
                    <span className="font-semibold">${result.utilizationFeeUSD}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-[#1E293B]">
                    <div>
                      <span className="text-white/40">Подбор автомобиля</span>
                      <span className="text-xs text-white/50 ml-2">
                        ({result.carSelectionFeeBYN} BYN)
                      </span>
                    </div>
                    <span className="font-semibold">${result.carSelectionFeeUSD}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-[#1E293B]">
                    <span className="text-white/40">Сертификация (СБКТС)</span>
                    <span className="font-semibold">${result.certification}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-[#1E293B]">
                    <span className="text-white/40">Брокер + СВХ</span>
                    <span className="font-semibold">${result.broker + result.parking}</span>
                  </div>
                </div>

                {/* Total */}
                <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-5 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">Итого примерно:</span>
                    <span className="text-3xl font-bold">${result.total.toLocaleString()}</span>
                  </div>
                </div>

                <p className="text-xs text-white/50 mb-2">
                  * Расчёт является предварительным. Точную стоимость уточняйте у менеджера.
                </p>
                <p className="text-xs text-white/50 mb-4">
                  Курс НБРБ: 1 USD = {result.usdToByn?.toFixed(4)} BYN
                </p>

                <div className="flex flex-col gap-3">
                  <Button
                    onClick={scrollToContact}
                    variant="outline"
                    className="w-full h-12 rounded-none border-white/30 text-white hover:bg-white/10 hover:text-white"
                    data-testid="result-cta-btn"
                  >
                    Получить точный расчёт
                  </Button>
                  <Button
                    onClick={exportToPDF}
                    className="w-full h-12 rounded-none bg-[#2563EB] hover:bg-[#1D4ED8] text-white flex items-center justify-center gap-2"
                    data-testid="export-pdf-btn"
                  >
                    <Download className="w-5 h-5" />
                    Скачать PDF
                  </Button>
                </div>
              </div>
            ) : (
              <div 
                className="bg-[#0A0A0A] rounded-none p-6 md:p-8 flex-1 flex flex-col items-center justify-center text-center"
                data-testid="calculator-placeholder"
              >
                <div className="w-20 h-20 bg-[#1A1A1A] border border-white/10 rounded-full flex items-center justify-center mb-6">
                  <Calculator className="w-10 h-10 text-white/40" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  Заполните параметры
                </h3>
                <p className="text-white/50 max-w-sm">
                  Выберите страну, возраст авто, объём двигателя и укажите стоимость для расчёта
                </p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Info Banner */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="mt-12 bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] rounded-2xl p-6 md:p-8 text-white"
          data-testid="calculator-info-banner"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold mb-1">Нужен точный расчёт?</h3>
              <p className="text-white/80">
                Наши специалисты рассчитают стоимость под ваш конкретный автомобиль
              </p>
            </div>
            <Button
              onClick={scrollToContact}
              className="bg-[#0F0F0F] text-[#2563EB] hover:bg-[#0A0A0A] rounded-full px-8 whitespace-nowrap"
              data-testid="banner-cta-btn"
            >
              Связаться с нами
            </Button>
          </div>
        </motion.div>

        {/* Tariffs Table */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-12"
          data-testid="tariffs-info"
        >
          <h3 className="text-xl font-bold text-white mb-6 text-center">
            Таможенные ставки ЕАЭС
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* До 3 лет */}
            <div className="bg-[#0A0A0A] rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-[#2563EB]" />
                <h4 className="font-semibold text-white">До 3 лет</h4>
              </div>
              <p className="text-xs text-white/50 mb-4">
                % от стоимости, но не менее ставки за см³
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-white/50">до €8 500</span>
                  <span className="font-medium text-white">54% / €2.5</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-white/50">до €16 700</span>
                  <span className="font-medium text-white">48% / €3.5</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-white/50">до €42 300</span>
                  <span className="font-medium text-white">48% / €5.5</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-white/50">более</span>
                  <span className="font-medium text-white">48% / €7.5-20</span>
                </div>
              </div>
            </div>

            {/* 3-5 лет */}
            <div className="bg-[#0A0A0A] rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-[#F97316]" />
                <h4 className="font-semibold text-white">3-5 лет</h4>
              </div>
              <p className="text-xs text-white/50 mb-4">
                Ставка за см³ по объёму двигателя
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-white/50">до 1500 см³</span>
                  <span className="font-medium text-white">€1.5-1.7</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-white/50">1500-2300 см³</span>
                  <span className="font-medium text-white">€2.5-2.7</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-white/50">2300-3000 см³</span>
                  <span className="font-medium text-white">€3.0</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-white/50">более 3000 см³</span>
                  <span className="font-medium text-white">€3.6</span>
                </div>
              </div>
            </div>

            {/* Более 5 лет */}
            <div className="bg-[#0A0A0A] border border-red-500/30 rounded-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-red-500" />
                <h4 className="font-semibold text-white">Более 5 лет</h4>
              </div>
              <p className="text-xs text-white/50 mb-4">
                Повышенные ставки за см³
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-white/50">до 1500 см³</span>
                  <span className="font-medium text-red-400">€3.0-3.2</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-white/50">1500-2300 см³</span>
                  <span className="font-medium text-red-400">€3.5-4.8</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-white/50">2300-3000 см³</span>
                  <span className="font-medium text-red-400">€5.0</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-white/50">более 3000 см³</span>
                  <span className="font-medium text-red-400">€5.7</span>
                </div>
              </div>
            </div>

            {/* Электромобили */}
            <div className="bg-[#0A0A0A] border border-green-500/30 rounded-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-5 h-5 text-green-500" />
                <h4 className="font-semibold text-white">Электромобили</h4>
              </div>
              <p className="text-xs text-white/50 mb-4">
                Указ №92/428, Решение ЕЭК №111
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-white/50">До 5 лет</span>
                  <span className="font-bold text-green-400">0% пошлина</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-white/50">Старше 5 лет</span>
                  <span className="font-medium text-orange-400">15%</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-white/50">НДС</span>
                  <span className="font-bold text-green-400">0%</span>
                </div>
              </div>
              <p className="text-xs text-green-400 mt-3 font-medium">
                ⚡ Освобождение до 31.12.2028!
              </p>
            </div>
          </div>

          {/* Utilization Fee Info */}
          <div className="mt-6 p-4 bg-[#0A0A0A] rounded-none">
            <p className="text-sm text-white/50">
              <span className="font-medium text-white">Утилизационный сбор РБ (с 10.02.2025):</span>{" "}
              До 3 лет — 544 BYN, 3-7 лет — 1089 BYN, старше 7 лет — 2757 BYN. 
              Конвертация в USD по курсу НБРБ.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
